import React, { useState, useEffect, useRef } from 'react';
import { useParams, Link } from 'react-router-dom';
import { jugadoresAPI } from '../../services/api';
import PhotoCapture from '../../components/PhotoCapture';

const API_URL = process.env.REACT_APP_API_URL || 'https://lossaliasfutbolclub.com/api';
const UPLOADS_BASE = API_URL.replace('/api', '') + '/uploads/documentos/';

const JugadorDetalle = () => {
  const { id } = useParams();
  const [jugador, setJugador] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [successMsg, setSuccessMsg] = useState('');
  const [uploadingFoto, setUploadingFoto] = useState(false);
  const [updatingStatus, setUpdatingStatus] = useState(false);
  const fotoInputRef = useRef(null);
  const cameraInputRef = useRef(null);

  useEffect(() => {
    loadJugador();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);

  const loadJugador = async () => {
    try {
      setLoading(true);
      setError('');
      const response = await jugadoresAPI.getById(id);
      setJugador(response.data);
    } catch (err) {
      setError('Error cargando datos del jugador');
    } finally {
      setLoading(false);
    }
  };

  const handleFotoUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;

    if (file.size > 5 * 1024 * 1024) {
      setError('La foto no debe superar los 5MB');
      return;
    }

    try {
      setUploadingFoto(true);
      setError('');
      setSuccessMsg('');
      const formData = new FormData();
      formData.append('foto', file);
      await jugadoresAPI.uploadFoto(id, formData);
      setSuccessMsg('Foto actualizada correctamente');
      await loadJugador();
    } catch (err) {
      setError(err.response?.data?.error || 'Error subiendo la foto');
    } finally {
      setUploadingFoto(false);
      e.target.value = '';
    }
  };

  const handleStatusChange = async (newStatus) => {
    try {
      setUpdatingStatus(true);
      setError('');
      setSuccessMsg('');
      await jugadoresAPI.update(id, { status: newStatus });
      setSuccessMsg(`Condicion cambiada a ${statusLabels[newStatus]}`);
      await loadJugador();
    } catch (err) {
      setError(err.response?.data?.error || 'Error actualizando condicion');
    } finally {
      setUpdatingStatus(false);
    }
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return '--';
    return new Date(dateStr).toLocaleDateString('es-VE');
  };

  const calcularEdad = (fechaNacimiento) => {
    if (!fechaNacimiento) return '--';
    const hoy = new Date();
    const nacimiento = new Date(fechaNacimiento);
    let edad = hoy.getFullYear() - nacimiento.getFullYear();
    const m = hoy.getMonth() - nacimiento.getMonth();
    if (m < 0 || (m === 0 && hoy.getDate() < nacimiento.getDate())) edad--;
    return edad;
  };

  const handlePhotoReady = async (blob) => {
    const formData = new FormData();
    formData.append('foto', blob, 'foto_jugador.jpg');
    setUploadingFoto(true);
    try {
      await jugadoresAPI.uploadFoto(id, formData);
      setSuccessMsg('Foto actualizada correctamente');
      await loadJugador();
    } catch (err) {
      setError(err.response?.data?.error || 'Error subiendo la foto');
    } finally {
      setUploadingFoto(false);
    }
  };

  const getFotoUrl = (j) => {
    if (j.foto_url) {
      if (j.foto_url.startsWith('http')) return j.foto_url;
      return UPLOADS_BASE + j.foto_url;
    }
    return null;
  };

  const Campo = ({ label, value, fullWidth }) => (
    <div className={fullWidth ? 'md:col-span-2' : ''}>
      <p className="text-xs font-semibold text-gray-500 uppercase tracking-wide">{label}</p>
      <p className="text-gray-900 mt-1">{value || '--'}</p>
    </div>
  );

  const CampoBool = ({ label, value }) => (
    <div>
      <p className="text-xs font-semibold text-gray-500 uppercase tracking-wide">{label}</p>
      <p className={`mt-1 font-medium ${value ? 'text-green-600' : 'text-gray-400'}`}>
        {value ? 'Si' : 'No'}
      </p>
    </div>
  );

  const statusLabels = { regular: 'Regular', media_beca: 'Media Beca', becado: 'Becado' };
  const statusColors = {
    regular: 'bg-green-100 text-green-800 border-green-300',
    media_beca: 'bg-yellow-100 text-yellow-800 border-yellow-300',
    becado: 'bg-blue-100 text-blue-800 border-blue-300',
  };
  const statusButtonColors = {
    regular: 'bg-green-500 hover:bg-green-600 text-white',
    media_beca: 'bg-yellow-500 hover:bg-yellow-600 text-white',
    becado: 'bg-blue-500 hover:bg-blue-600 text-white',
  };

  if (loading) {
    return (
      <div className="flex justify-center items-center py-20">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-lsfc-blue-600"></div>
      </div>
    );
  }

  if (error && !jugador) {
    return (
      <div className="text-center py-20">
        <div className="text-5xl mb-4">⚠️</div>
        <p className="text-red-600 text-lg mb-4">{error}</p>
        <Link to="/admin/jugadores" className="text-lsfc-blue-600 hover:underline font-medium">
          ← Volver a jugadores
        </Link>
      </div>
    );
  }

  if (!jugador) return null;

  const j = jugador;
  const fotoUrl = getFotoUrl(j);

  return (
    <div className="max-w-5xl mx-auto space-y-6">
      {/* Mensajes */}
      {successMsg && (
        <div className="bg-green-50 border border-green-300 text-green-800 px-4 py-3 rounded-lg font-medium">
          {successMsg}
        </div>
      )}
      {error && (
        <div className="bg-red-50 border border-red-300 text-red-700 px-4 py-3 rounded-lg">
          {error}
        </div>
      )}

      {/* Header con foto */}
      <div className="bg-white rounded-xl shadow-lg overflow-hidden">
        <div className="bg-gradient-to-r from-lsfc-blue-600 to-lsfc-blue-800 p-6">
          <Link to="/admin/jugadores" className="text-blue-200 hover:text-white text-sm font-medium mb-4 inline-block">
            ← Volver a jugadores
          </Link>
          <div className="flex flex-col sm:flex-row items-start sm:items-center gap-6">
            {/* Foto grande con boton de cambiar */}
            <div className="relative group flex-shrink-0">
              <div className="w-28 h-28 rounded-full overflow-hidden border-4 border-white shadow-lg">
                {fotoUrl ? (
                  <img src={fotoUrl} alt={j.nombres} className="w-full h-full object-cover" />
                ) : (
                  <div className="w-full h-full flex items-center justify-center bg-lsfc-blue-200">
                    <span className="text-5xl">⚽</span>
                  </div>
                )}
              </div>
              {/* Overlay para cambiar foto */}
              <div className="absolute inset-0 rounded-full bg-black/50 flex flex-col items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity cursor-pointer"
                onClick={() => fotoInputRef.current?.click()}>
                {uploadingFoto ? (
                  <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-white"></div>
                ) : (
                  <>
                    <span className="text-white text-2xl">📷</span>
                    <span className="text-white text-xs mt-1 font-medium">Cambiar</span>
                  </>
                )}
              </div>
              <input ref={fotoInputRef} type="file" accept="image/*" onChange={handleFotoUpload} className="hidden" />
              <input ref={cameraInputRef} type="file" accept="image/*" capture="environment" onChange={handleFotoUpload} className="hidden" />
            </div>

            {/* Info principal */}
            <div className="flex-1">
              <h1 className="text-2xl font-bold text-white">
                {j.apellidos} {j.nombres}
              </h1>
              <p className="text-blue-200 mt-1">Cedula: {j.cedula}</p>
              <div className="flex flex-wrap items-center gap-3 mt-3">
                <span className={`px-3 py-1 rounded-full text-sm font-bold border ${statusColors[j.status] || ''}`}>
                  {statusLabels[j.status] || j.status}
                </span>
                {j.categoria && (
                  <span className="px-3 py-1 rounded-full text-sm font-bold bg-white/20 text-white border border-white/30">
                    {j.categoria}
                  </span>
                )}
                {j.activo ? (
                  <span className="px-3 py-1 rounded-full text-sm font-bold bg-green-100 text-green-800 border border-green-300">Activo</span>
                ) : (
                  <span className="px-3 py-1 rounded-full text-sm font-bold bg-red-100 text-red-800 border border-red-300">Inactivo</span>
                )}
                <span className="text-blue-200 text-sm">
                  {calcularEdad(j.fecha_nacimiento)} anos | {j.sexo === 'M' ? 'Masculino' : 'Femenino'}
                </span>
              </div>
            </div>
          </div>
        </div>

        {/* Botones de foto y condicion */}
        <div className="p-4 bg-gray-50 border-t flex flex-col sm:flex-row gap-4">
          {/* Botones de foto */}
          <div className="flex-1">
            <p className="text-xs font-semibold text-gray-500 uppercase mb-2">Foto del Jugador</p>
            <div className="flex gap-2 flex-wrap">
              <PhotoCapture
                currentPhotoUrl={fotoUrl}
                onPhotoReady={handlePhotoReady}
                playerName={j.nombres}
              />
              <button onClick={() => cameraInputRef.current?.click()} disabled={uploadingFoto}
                className="px-3 py-2 bg-lsfc-blue-600 text-white rounded-lg hover:bg-lsfc-blue-700 text-sm font-medium disabled:opacity-50">
                📷 Sin procesar
              </button>
              <button onClick={() => fotoInputRef.current?.click()} disabled={uploadingFoto}
                className="px-3 py-2 bg-gray-200 text-gray-700 rounded-lg hover:bg-gray-300 text-sm font-medium disabled:opacity-50">
                📁 Subir Archivo
              </button>
            </div>
          </div>

          {/* Cambiar condicion */}
          <div className="flex-1">
            <p className="text-xs font-semibold text-gray-500 uppercase mb-2">Cambiar Condicion</p>
            <div className="flex gap-2">
              {Object.entries(statusLabels).map(([key, label]) => (
                <button key={key} onClick={() => handleStatusChange(key)}
                  disabled={updatingStatus || j.status === key}
                  className={`px-3 py-2 rounded-lg text-sm font-medium transition-all disabled:opacity-40 ${
                    j.status === key
                      ? 'ring-2 ring-offset-1 ring-gray-400 ' + statusButtonColors[key]
                      : statusButtonColors[key]
                  }`}>
                  {label}
                </button>
              ))}
            </div>
          </div>
        </div>
      </div>

      {/* Datos del Alumno */}
      <div className="bg-white rounded-xl shadow-lg p-6">
        <h2 className="text-lg font-bold text-lsfc-blue-700 mb-4 flex items-center gap-2">
          <span>⚽</span> Datos Personales
        </h2>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <Campo label="Apellidos" value={j.apellidos} />
          <Campo label="Nombres" value={j.nombres} />
          <Campo label="Cedula" value={j.cedula} />
          <Campo label="Tiene Cedula Propia" value={j.tiene_cedula_propia ? 'Si' : 'No'} />
          <Campo label="Fecha de Nacimiento" value={`${formatDate(j.fecha_nacimiento)} (${calcularEdad(j.fecha_nacimiento)} anos)`} />
          <Campo label="Sexo" value={j.sexo === 'M' ? 'Masculino' : j.sexo === 'F' ? 'Femenino' : '--'} />
          <Campo label="Lugar de Nacimiento" value={j.lugar_nacimiento} />
          <Campo label="Estado" value={j.estado} />
          <Campo label="Pais" value={j.pais} />
          <Campo label="Telefono Celular" value={j.telefono_celular} />
          <Campo label="Telefono Habitacion" value={j.telefono_habitacion} />
          <Campo label="Otro Telefono" value={j.otro_telefono} />
          <Campo label="Colegio Actual" value={j.colegio_actual} />
          <Campo label="Fecha de Inscripcion" value={formatDate(j.fecha_inscripcion)} />
          <Campo label="Direccion" value={j.direccion_habitacion} fullWidth />
        </div>
      </div>

      {/* Representantes */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <div className="bg-white rounded-xl shadow-lg p-6">
          <h2 className="text-lg font-bold text-blue-700 mb-4 flex items-center gap-2">
            <span>👨</span> Padre
          </h2>
          {j.padre_nombre ? (
            <div className="grid grid-cols-1 gap-3">
              <Campo label="Nombre" value={`${j.padre_nombre} ${j.padre_apellido || ''}`} />
              <Campo label="Cedula" value={j.padre_cedula} />
              <Campo label="Telefono" value={j.padre_telefono} />
              <Campo label="Email" value={j.padre_email} />
            </div>
          ) : (
            <p className="text-gray-400 italic">No registrado</p>
          )}
        </div>

        <div className="bg-white rounded-xl shadow-lg p-6">
          <h2 className="text-lg font-bold text-pink-700 mb-4 flex items-center gap-2">
            <span>👩</span> Madre
          </h2>
          {j.madre_nombre ? (
            <div className="grid grid-cols-1 gap-3">
              <Campo label="Nombre" value={`${j.madre_nombre} ${j.madre_apellido || ''}`} />
              <Campo label="Cedula" value={j.madre_cedula} />
              <Campo label="Telefono" value={j.madre_telefono} />
              <Campo label="Email" value={j.madre_email} />
            </div>
          ) : (
            <p className="text-gray-400 italic">No registrado</p>
          )}
        </div>
      </div>

      {/* Informacion Medica */}
      <div className="bg-white rounded-xl shadow-lg p-6">
        <h2 className="text-lg font-bold text-green-700 mb-4 flex items-center gap-2">
          <span>🏥</span> Informacion Medica
        </h2>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <CampoBool label="Tiene Seguro Medico" value={j.tiene_seguro} />
          {j.tiene_seguro && (
            <>
              <Campo label="Compania de Seguro" value={j.compania_seguro} />
              <Campo label="Numero de Poliza" value={j.numero_poliza} />
            </>
          )}
          <CampoBool label="Padece Enfermedades" value={j.padece_enfermedades} />
          {j.padece_enfermedades && (
            <Campo label="Tipo de Enfermedades" value={j.tipo_enfermedades} fullWidth />
          )}
          <Campo label="Alergias" value={j.alergias} fullWidth />
          <Campo label="Tipo de Sangre" value={j.tipo_sangre} />
          <Campo label="Grupo Sanguineo" value={j.grupo_sangre} />
        </div>
      </div>

      {/* Contactos de Emergencia */}
      <div className="bg-white rounded-xl shadow-lg p-6">
        <h2 className="text-lg font-bold text-red-700 mb-4 flex items-center gap-2">
          <span>🚨</span> Contactos de Emergencia
        </h2>
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          <div className="bg-red-50 rounded-lg p-4">
            <h3 className="font-semibold text-red-800 mb-3">Contacto 1</h3>
            <div className="grid grid-cols-1 gap-3">
              <Campo label="Nombre" value={j.emergencia1_nombre} />
              <Campo label="Parentesco" value={j.emergencia1_parentesco} />
              <Campo label="Cedula" value={j.emergencia1_cedula} />
              <Campo label="Telefono" value={j.emergencia1_telefono} />
            </div>
          </div>
          {j.emergencia2_nombre && (
            <div className="bg-gray-50 rounded-lg p-4">
              <h3 className="font-semibold text-gray-800 mb-3">Contacto 2</h3>
              <div className="grid grid-cols-1 gap-3">
                <Campo label="Nombre" value={j.emergencia2_nombre} />
                <Campo label="Parentesco" value={j.emergencia2_parentesco} />
                <Campo label="Cedula" value={j.emergencia2_cedula} />
                <Campo label="Telefono" value={j.emergencia2_telefono} />
              </div>
            </div>
          )}
        </div>

        <div className="mt-6 bg-purple-50 rounded-lg p-4">
          <h3 className="font-semibold text-purple-800 mb-3">Medicamentos Autorizados</h3>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <Campo label="Medicamento para Alergias" value={j.medicamento_alergia} />
            <Campo label="Dosis" value={j.dosis_alergia} />
            <Campo label="Medicamento para Dolor de Cabeza" value={j.medicamento_dolor_cabeza} />
            <Campo label="Dosis" value={j.dosis_dolor_cabeza} />
          </div>
        </div>
      </div>

      {/* Autorizaciones */}
      <div className="bg-white rounded-xl shadow-lg p-6">
        <h2 className="text-lg font-bold text-purple-700 mb-4 flex items-center gap-2">
          <span>📝</span> Autorizaciones
        </h2>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <div className={`p-4 rounded-lg border-2 ${j.autorizacion_medica ? 'border-green-300 bg-green-50' : 'border-red-300 bg-red-50'}`}>
            <p className="font-medium text-sm">{j.autorizacion_medica ? '✅' : '❌'} Autorizacion Medica</p>
          </div>
          <div className={`p-4 rounded-lg border-2 ${j.autorizacion_imagen ? 'border-green-300 bg-green-50' : 'border-red-300 bg-red-50'}`}>
            <p className="font-medium text-sm">{j.autorizacion_imagen ? '✅' : '❌'} Autorizacion de Imagen</p>
          </div>
          <div className={`p-4 rounded-lg border-2 ${j.autorizacion_medicamentos ? 'border-green-300 bg-green-50' : 'border-gray-200 bg-gray-50'}`}>
            <p className="font-medium text-sm">{j.autorizacion_medicamentos ? '✅' : '⬜'} Autorizacion Medicamentos</p>
          </div>
        </div>
      </div>

      {/* Metadata */}
      <div className="bg-gray-50 rounded-xl p-4 text-xs text-gray-500">
        <div className="flex flex-wrap gap-4">
          <span>ID: {j.id}</span>
          <span>Creado: {formatDate(j.created_at)}</span>
          <span>Actualizado: {formatDate(j.updated_at)}</span>
        </div>
      </div>
    </div>
  );
};

export default JugadorDetalle;
